// src/components/BlocDetailsModal.jsx
import React from "react";
import axios from "axios";
import {
  Alert,
  Badge,
  Card,
  Descriptions,
  Image,
  Modal,
  Row,
  Col,
  Skeleton,
  Space,
  Tag,
  Typography,
  Empty,
} from "antd";
import { useTranslation } from "react-i18next";
import {
  ClockCircleOutlined,
  FieldTimeOutlined,
  PlayCircleOutlined,
  CheckCircleTwoTone,
} from "@ant-design/icons";
import Constants from "../Utils/Constants";

const isMinioImageKey = (value) =>
  typeof value === "string" && value.startsWith("images/");

const isMinioVideoKey = (value) =>
  typeof value === "string" &&
  (value.startsWith("videos/") || value.startsWith("sessions/"));

const normalizeLegacyUrl = (url) => {
  if (!url) return null;

  if (url.startsWith("http")) {
    return url;
  }

  return `${Constants.SERVER_URL.replace(/\/$/, "")}/${url.replace(
    /^\/+/,
    ""
  )}`;
};

export default function BlocDetailsModal({ open, blocKey, onClose }) {
  const { t, i18n } = useTranslation("seances", { useSuspense: false });
  const token = localStorage.getItem("token");

  const headers = React.useMemo(
    () => ({
      Authorization: "Bearer " + token,
      "Accept-Language": i18n.language,
    }),
    [token, i18n.language]
  );

  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const [imageUrls, setImageUrls] = React.useState({});
  const [videoUrls, setVideoUrls] = React.useState({});

  const getSignedUrl = async (key, bucket) => {
    const res = await axios.get(
      `${Constants.SERVER_URL}/api/minio/presign-download`,
      {
        params: {
          bucket,
          key,
        },
        headers,
      }
    );

    return res.data.downloadUrl;
  };

  React.useEffect(() => {
    if (!open || !blocKey) return;

    setLoading(true);
    setErr(null);
    setData(null);
    setImageUrls({});
    setVideoUrls({});

    axios
      .get(`${Constants.SERVER_URL}/bloc/${encodeURIComponent(blocKey)}`, {
        headers,
      })
      .then((res) => setData(res.data || null))
      .catch((error) => setErr(error))
      .finally(() => setLoading(false));
  }, [open, blocKey, headers]);

  React.useEffect(() => {
    if (!data) return;

    let cancelled = false;

    async function loadMedia() {
      const exercises = Array.isArray(data.exercises)
        ? data.exercises
        : Array.isArray(data.Exercises)
        ? data.Exercises
        : Array.isArray(data.Exercise_Blocs)
        ? data.Exercise_Blocs.map((row) => row.Exercise).filter(Boolean)
        : [];

      const imageEntries = [];
      const videoEntries = [];

      for (const exercise of exercises) {
        const exerciseInfo = exercise?.Exercise || exercise;

        if (!exerciseInfo?.id) continue;

        if (exerciseInfo.imageUrl) {
          try {
            const imageUrl = isMinioImageKey(exerciseInfo.imageUrl)
              ? await getSignedUrl(exerciseInfo.imageUrl, "rxapa-assets")
              : normalizeLegacyUrl(exerciseInfo.imageUrl);

            imageEntries.push([exerciseInfo.id, imageUrl]);
          } catch {
            imageEntries.push([exerciseInfo.id, null]);
          }
        }

        if (exerciseInfo.videoUrl) {
          try {
            const videoUrl = isMinioVideoKey(exerciseInfo.videoUrl)
              ? await getSignedUrl(exerciseInfo.videoUrl, "rxapa-videos")
              : normalizeLegacyUrl(exerciseInfo.videoUrl);

            videoEntries.push([exerciseInfo.id, videoUrl]);
          } catch {
            videoEntries.push([exerciseInfo.id, null]);
          }
        }
      }

      if (!cancelled) {
        setImageUrls(Object.fromEntries(imageEntries));
        setVideoUrls(Object.fromEntries(videoEntries));
      }
    }

    loadMedia();

    return () => {
      cancelled = true;
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const getMeta = (exercise) =>
    exercise?.Exercise_Bloc || exercise?.pivot || exercise || {};

  const exercises = Array.isArray(data?.exercises)
    ? data.exercises
    : Array.isArray(data?.Exercises)
    ? data.Exercises
    : Array.isArray(data?.Exercise_Blocs)
    ? data.Exercise_Blocs
    : [];

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={onClose}
      okText={t("label_close") || "Fermer"}
      title={
        data?.name
          ? `${t("label_bloc") || "Bloc"} : ${data.name}`
          : t("label_bloc_details") || "Détails du bloc"
      }
      destroyOnClose
      width={980}
    >
      {loading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : err ? (
        <Alert
          type="error"
          message={
            t("label_error_loading") || "Erreur lors du chargement du bloc."
          }
        />
      ) : !data ? (
        <Alert
          type="warning"
          message={t("label_bloc_not_found") || "Bloc introuvable."}
        />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item
              label={t("label_description") || "Description"}
              span={2}
            >
              {data.description || "—"}
            </Descriptions.Item>
          </Descriptions>

          <Typography.Title level={5} style={{ margin: 0 }}>
            {t("label_bloc_exercises") || "Exercices du bloc"}
          </Typography.Title>

          {exercises.length > 0 ? (
            <Row gutter={[16, 16]}>
              {exercises.map((row) => {
                const exercise = row?.Exercise || row?.exercise || row;
                const meta = getMeta(row);

                if (!exercise?.name) {
                  return (
                    <Col key={row?.id || row?.key || Math.random()} span={24}>
                      <Alert
                        type="warning"
                        message="Exercice introuvable dans ce bloc."
                      />
                    </Col>
                  );
                }

                const series = meta?.numberOfSeries;
                const reps = meta?.numberOfRepetition;
                const minutes = meta?.minutes;
                const rest = meta?.restingInstruction || meta?.rest;
                const required = !!meta?.required;

                const imageUrl = imageUrls[exercise.id];
                const videoUrl = videoUrls[exercise.id];

                return (
                  <Col key={exercise.id || row.key} xs={24} sm={12}>
                    <Badge.Ribbon
                      text={
                        required
                          ? t("label_required") || "Obligatoire"
                          : t("label_optional") || "Optionnel"
                      }
                      color={required ? "red" : "blue"}
                    >
                      <Card hoverable>
                        <Space
                          direction="vertical"
                          size={8}
                          style={{ width: "100%" }}
                        >
                          {imageUrl ? (
                            <Image
                              src={imageUrl}
                              alt={exercise.name}
                              height={160}
                              style={{ objectFit: "cover" }}
                              preview
                            />
                          ) : (
                            <Empty
                              image={Empty.PRESENTED_IMAGE_SIMPLE}
                              description="Aucune image"
                            />
                          )}

                          <Typography.Text strong ellipsis title={exercise.name}>
                            {exercise.name}
                          </Typography.Text>

                          <Space wrap size={[8, 4]}>
                            {typeof series === "number" && (
                              <Tag>
                                📦 {series} {t("label_series") || "séries"}
                              </Tag>
                            )}

                            {typeof reps === "number" && (
                              <Tag>
                                🔁 {reps} {t("label_reps") || "reps"}
                              </Tag>
                            )}

                            {typeof minutes === "number" && minutes > 0 && (
                              <Tag icon={<ClockCircleOutlined />}>
                                {minutes} {t("label_minutes") || "min"}
                              </Tag>
                            )}

                            {rest ? (
                              <Tag icon={<FieldTimeOutlined />}>
                                {t("label_rest") || "Repos"}: {String(rest)}
                              </Tag>
                            ) : null}

                            {required && (
                              <CheckCircleTwoTone twoToneColor="#f5222d" />
                            )}
                          </Space>

                          {videoUrl && (
                            <Typography.Link
                              href={videoUrl}
                              target="_blank"
                              rel="noreferrer"
                            >
                              <PlayCircleOutlined />{" "}
                              {t("label_view_video") || "Voir la vidéo"}
                            </Typography.Link>
                          )}

                          {exercise.description && (
                            <Typography.Paragraph ellipsis={{ rows: 3 }}>
                              {exercise.description}
                            </Typography.Paragraph>
                          )}
                        </Space>
                      </Card>
                    </Badge.Ribbon>
                  </Col>
                );
              })}
            </Row>
          ) : (
            <Alert
              type="info"
              message={
                t("label_no_exercises") || "Aucun exercice dans ce bloc."
              }
            />
          )}
        </Space>
      )}
    </Modal>
  );
}